import React, { useEffect, useMemo, useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    Stack,
    Box,
    Switch,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Button,
    Alert,
} from '@mui/material';
import { NotificationsActive, Email, Sms, NotificationImportant } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetNotificationPreferences } from '../../hooks/customer/useGetNotificationPreferences';
import { useUpdateNotificationPreferences } from '../../hooks/customer/useUpdateNotificationPreferences';

// Events the customer can opt out of. Mirrors Backend Sprint 13's
// supported events. Add a row here as new event codes ship.
const EVENT_ROWS = [
    { code: 'order.placed',                 labelEn: 'Order placed',         labelAr: 'تم إنشاء الطلب' },
    { code: 'order.shipped',                labelEn: 'Order shipped',        labelAr: 'تم شحن الطلب' },
    { code: 'order.delivered',              labelEn: 'Order delivered',      labelAr: 'تم تسليم الطلب' },
    { code: 'order.cancelled',              labelEn: 'Order cancelled',      labelAr: 'تم إلغاء الطلب' },
    { code: 'order.refunded',               labelEn: 'Order refunded',       labelAr: 'تم استرداد الطلب' },
    { code: 'return.submitted',             labelEn: 'Return submitted',     labelAr: 'تم تقديم طلب إرجاع' },
    { code: 'promotion.announcement',       labelEn: 'Promotions',           labelAr: 'العروض الترويجية' },
    { code: 'low_stock.back_in_stock',      labelEn: 'Back in stock alerts', labelAr: 'تنبيهات التوفر' },
];

const CHANNELS = [
    { key: 'email', Icon: Email,                   labelEn: 'Email', labelAr: 'البريد' },
    { key: 'sms',   Icon: Sms,                     labelEn: 'SMS',   labelAr: 'رسالة' },
    { key: 'push',  Icon: NotificationImportant,   labelEn: 'Push',  labelAr: 'إشعار' },
];

// Build a complete matrix from the partial server response so missing
// keys default to "on" (the customer is opted in by default).
const buildDraft = (preferences) => {
    const next = {};
    EVENT_ROWS.forEach((row) => {
        const server = preferences?.[row.code] || {};
        next[row.code] = {
            email: server.email ?? true,
            sms:   server.sms   ?? true,
            push:  server.push  ?? true,
        };
    });
    return next;
};

const NotificationPreferences = () => {
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const navigate = useNavigate();

    const { preferences, isLoading, error } = useGetNotificationPreferences();
    const updateMutation = useUpdateNotificationPreferences();

    const initial = useMemo(() => buildDraft(preferences), [preferences]);
    const [draft, setDraft] = useState(initial);

    useEffect(() => {
        setDraft(initial);
    }, [initial]);

    const isDirty = useMemo(
        () => JSON.stringify(initial) !== JSON.stringify(draft),
        [initial, draft]
    );

    const handleToggle = (event, channel) => (e) => {
        const checked = e.target.checked;
        setDraft((prev) => ({
            ...prev,
            [event]: { ...(prev[event] || {}), [channel]: checked },
        }));
    };

    const handleReset = () => setDraft(initial);
    const handleSave = () => {
        if (!isDirty) return;
        updateMutation.mutate(draft);
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
            <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                    <NotificationsActive color="primary" />
                    <Box>
                        <Typography variant="h5" fontWeight={800}>
                            {t('profile.notifications', 'Notification preferences')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t(
                                'profile.notificationsSubtitle',
                                'Pick which events reach you, and on which channel.'
                            )}
                        </Typography>
                    </Box>
                </Stack>

                {isLoading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Alert severity="error">
                        {t('profile.notificationsLoadError', 'Could not load preferences')}
                    </Alert>
                ) : (
                    <>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t('profile.event', 'Event')}
                                        </TableCell>
                                        {CHANNELS.map((c) => (
                                            <TableCell key={c.key} align="center" sx={{ fontWeight: 700 }}>
                                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                                    <c.Icon fontSize="small" />
                                                    <span>{isArabic ? c.labelAr : c.labelEn}</span>
                                                </Stack>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {EVENT_ROWS.map((row) => {
                                        const allOff = CHANNELS.every((c) => !draft[row.code]?.[c.key]);
                                        return (
                                            <TableRow key={row.code}>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {isArabic ? row.labelAr : row.labelEn}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                                                        >
                                                            {row.code}
                                                        </Typography>
                                                        {allOff && (
                                                            <Typography
                                                                variant="caption"
                                                                color="warning.main"
                                                                sx={{ display: 'block', fontWeight: 700 }}
                                                            >
                                                                {t('profile.allChannelsOff', 'All channels off — you will not be notified.')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                {CHANNELS.map((c) => (
                                                    <TableCell key={c.key} align="center">
                                                        <Switch
                                                            size="small"
                                                            checked={Boolean(draft[row.code]?.[c.key])}
                                                            onChange={handleToggle(row.code, c.key)}
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            sx={{ mt: 3 }}
                            justifyContent="flex-end"
                        >
                            <Button
                                variant="text"
                                onClick={() => navigate('/profile')}
                                disabled={updateMutation.isPending}
                            >
                                {t('common.back', 'Back')}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleReset}
                                disabled={!isDirty || updateMutation.isPending}
                            >
                                {t('common.reset', 'Reset')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={!isDirty || updateMutation.isPending}
                            >
                                {updateMutation.isPending
                                    ? t('common.working', 'Working...')
                                    : t('common.save', 'Save')}
                            </Button>
                        </Stack>
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default NotificationPreferences;
